import { apiClient } from '../apiClient';

import type { ReadinessBreakdown } from './readiness';

export interface HomeNextSession {
  id: string; title: string; type: string;
  scheduled_at: string | null; trainer_name: string;
  meeting_url: string; recording_url?: string; topic_id: string;
  batch_name: string;
}
export interface HomeContinue {
  topic_id: string; topic_title: string; module_title: string;
  status: string; progress_pct: number;
}
export interface HomeDueItem {
  assessment_id: string; title: string; topic_id: string;
  type: string; due_date: string | null;
  state: 'overdue' | 'due_today' | 'upcoming';
  /** Owning batch — due items can span courses under "All Courses". */
  batch_id?: string; batch_name?: string;
  /** Practice hub category: problems | quizzes | assignments | projects */
  category?: string;
}
export interface HomeNextAction { label: string; delta: string; }
export interface HomeReadiness {
  score: number; tier: 'A' | 'B' | 'C' | 'needs_support';
  avg_score: number; completion_pct: number;
  on_time_rate: number; attendance_pct: number;
  next_actions: HomeNextAction[];
  /**
   * Per-component explanation of `score`. Optional because a client can be
   * newer than the API mid-deploy; absent means "explain nothing", never
   * "reconstruct the weights locally".
   */
  breakdown?: ReadinessBreakdown;
  /** Formula that produced `score`. Scores from different versions differ. */
  formula_version: number;
  /** Share of formula weight that had data behind it, 0..1. */
  data_coverage: number;
  /** false => show an empty state, never a score of 0. */
  has_data: boolean;
  /** ISO-8601; when the score was last computed. */
  computed_at?: string;
}
/**
 * What produced a skill score, and how much of it we can account for.
 *
 * `solves_recorded` is zero for every solve made before solve telemetry
 * shipped, so a low value means "we weren't recording", NOT "this was pasted".
 * That distinction is carried separately by `solves_pasted` and must survive
 * into any copy written against these numbers.
 */
export interface SkillEvidence {
  graded_items: number;
  assessment_pct: number;
  coding_total: number;
  coding_solved: number;
  solves_recorded: number;
  solves_pasted: number;
  /** Solves the student explained: asked what their code prints, answered right. */
  solves_explained: number;
  /**
   * Solves we can vouch for, counted server-side per solve. A solve that is
   * both recorded clean AND explained counts once, which is why this is not
   * derived from the fields above.
   */
  solves_accounted: number;
}
/** Present only when the skill has coding behind it. */
export interface HomeSkill { name: string; score: number; evidence?: SkillEvidence; }
export interface HomeStreak { current: number; longest: number; }
export interface HomeStats { problems_solved: number; batch_rank: number; course_pct: number; }
export interface HomeLeader { rank: number; username: string; points: number; }
export interface HomeHeatCell { date: string; count: number; }
export interface HomeBatchProgress {
  id: string;
  name: string;
  trainer_name: string;
  total_topics: number;
  completed_topics: number;
  progress_pct: number;
  next_session_at?: string | null;
}
export interface HomeAnnouncement {
  id: string;
  content: string;
  created_at: string; // ISO-8601 UTC
}
export interface DailyProblem {
  slug: string; title: string; difficulty: string;
  tags: string[]; solved: boolean; points: number;
}
export interface HomeTodayEvent {
  id: string;
  /** "session" | "assignment" | "contest" */
  type: string;
  title: string;
  time: string; // ISO-8601
  url?: string;
  /** Nav targets. Absent on contests, which aren't batch-scoped. */
  batch_id?: string;
  batch_name?: string;
  topic_id?: string;
  /** Practice hub category: problems | quizzes | assignments | projects */
  category?: string;
}

export interface HomeResponse {
  batch: { id: string; name: string };
  greeting_name: string;
  next_session: HomeNextSession | null;
  continue: HomeContinue | null;
  due_items: HomeDueItem[];
  today_events: HomeTodayEvent[];
  /** Next few scheduled things after today — sessions, deadlines, contests. */
  upcoming_events: HomeTodayEvent[];
  problem_of_the_day: DailyProblem | null;
  readiness: HomeReadiness | null;
  skills: HomeSkill[];
  streak: HomeStreak | null;
  stats: HomeStats;
  leaderboard_top: HomeLeader[];
  heatmap: HomeHeatCell[];
  batches: HomeBatchProgress[];
  announcements: HomeAnnouncement[];
}

export const homeApi = {
  getHome: (batchId?: string | null) =>
    apiClient.get<HomeResponse>('/api/v1/lms/home', {
      params: batchId ? { batch_id: batchId } : undefined,
    }),
  getDailyProblem: () =>
    apiClient.get<DailyProblem>('/api/v1/lms/problems/daily'),
};
